// Codec between the in-memory builder UI state and the canonical
// wire tree shape that BE-052 / B1 understands — Polish B2.
//
// UI state mirrors v1's TaskFilterPopover: an array of
// ``FilterGroup`` records, each with its own AND/OR ``groupOperator``
// and a list of ``FilterCondition`` rows, all joined by a
// top-level ``globalOperator``. The wire shape is the recursive
// ``{op: "and"|"or", children: [...]}`` validated by
// ``commitments.saved_filters.validate_saved_filter_definition``.
//
// Conversion intentionally collapses single-condition / single-group
// trees so the validator never sees empty wrappers — the BE rejects
// ``{and, [{and, []}]}``.

import { isMultiValue, getField } from "./fieldRegistry";
import type { UiOperator } from "./operators";
import type { FilterTree } from "../urlState";


export interface FilterCondition {
  /** Stable id used by React keys + delete handlers. */
  id: string;
  field: string;
  operator: UiOperator;
  value: unknown;
}


export interface FilterGroup {
  id: string;
  groupOperator: "AND" | "OR";
  conditions: FilterCondition[];
}


export interface FilterUiState {
  globalOperator: "AND" | "OR";
  groups: FilterGroup[];
}


export function emptyUiState(): FilterUiState {
  return {
    globalOperator: "AND",
    groups: [
      {
        id: makeId(),
        groupOperator: "AND",
        conditions: [],
      },
    ],
  };
}


export function makeId(): string {
  // Random-enough id for React keys; not security-sensitive.
  return `f_${Math.random().toString(36).slice(2, 10)}`;
}


// ── UI → wire ─────────────────────────────────────────────────────────────


export function uiToWire(state: FilterUiState): FilterTree | undefined {
  // Drop conditions that aren't fully filled in — the BE validator
  // would reject a leaf with no value or no field.
  const groups = state.groups
    .map(filledConditions)
    .filter((g) => g.conditions.length > 0);
  if (groups.length === 0) return undefined;
  // Single condition in a single group → emit the leaf.
  if (groups.length === 1 && groups[0].conditions.length === 1) {
    return conditionToLeaf(groups[0].conditions[0]);
  }
  // Single group with multiple conditions → just the group's
  // join, no top-level wrapper.
  if (groups.length === 1) {
    return {
      op: lower(groups[0].groupOperator),
      children: groups[0].conditions.map(conditionToLeaf),
    };
  }
  // Multiple groups → top-level join wrapping group nodes.
  return {
    op: lower(state.globalOperator),
    children: groups.map((g) => ({
      op: lower(g.groupOperator),
      children: g.conditions.map(conditionToLeaf),
    })),
  };
}


function filledConditions(g: FilterGroup): FilterGroup {
  return {
    ...g,
    conditions: g.conditions.filter((c) => isComplete(c)),
  };
}


export function isComplete(c: FilterCondition): boolean {
  if (!c.field || !c.operator) return false;
  if (c.operator === "is_empty" || c.operator === "is_not_empty") {
    return true;
  }
  if (c.value === null || c.value === undefined || c.value === "") {
    return false;
  }
  if (Array.isArray(c.value) && c.value.length === 0) return false;
  return true;
}


function conditionToLeaf(c: FilterCondition): FilterTree {
  const field = c.field;
  const desc = getField(field);
  switch (c.operator) {
    case "is_empty":
      return { op: "is_null", field };
    case "is_not_empty":
      return { op: "is_not_null", field };
    case "is_on":
      return { op: "eq", field, value: dateToISO(c.value) };
    case "is_before":
      return { op: "lt", field, value: dateToISO(c.value) };
    case "is_before_or_on":
      return { op: "lte", field, value: dateToISO(c.value) };
    case "is_after":
      return { op: "gt", field, value: dateToISO(c.value) };
    case "is_after_or_on":
      return { op: "gte", field, value: dateToISO(c.value) };
    case "contains":
      return { op: "contains", field, value: String(c.value ?? "") };
    case "is":
      // Multi-select fields → ``in`` over the value array; single
      // fields → ``eq`` with the value as-is.
      if (desc && isMultiValue(desc.valueType)) {
        const arr = Array.isArray(c.value) ? c.value : [c.value];
        return { op: "in", field, value: arr };
      }
      return { op: "eq", field, value: c.value };
    case "is_not":
      if (desc && isMultiValue(desc.valueType)) {
        const arr = Array.isArray(c.value) ? c.value : [c.value];
        return { op: "nin", field, value: arr };
      }
      return { op: "neq", field, value: c.value };
  }
}


function dateToISO(value: unknown): string {
  if (typeof value !== "string") return "";
  // ``<input type="date">`` emits ``YYYY-MM-DD``. The BE compares
  // against a ``DateTimeField``; append T00:00:00Z so the comparison
  // is unambiguous regardless of the user's timezone.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00Z`;
  }
  return value;
}


function lower(op: "AND" | "OR"): "and" | "or" {
  return op === "AND" ? "and" : "or";
}


// ── Wire → UI ─────────────────────────────────────────────────────────────


/** Best-effort decode of a wire tree back into the UI's flat
 * groups-of-conditions shape. Trees with deeper nesting collapse
 * into the closest representable shape; the page surfaces a
 * non-blocking notice when ``flattened`` is true. */
export function wireToUi(
  tree: FilterTree | undefined,
): { state: FilterUiState; flattened: boolean } {
  if (tree === undefined) {
    return { state: emptyUiState(), flattened: false };
  }
  // Single leaf → single condition in a single group.
  if (isLeaf(tree)) {
    const cond = leafToCondition(tree);
    return {
      state: {
        globalOperator: "AND",
        groups: [
          { id: makeId(), groupOperator: "AND", conditions: [cond] },
        ],
      },
      flattened: false,
    };
  }
  // Single AND/OR of leaves → one group.
  if (isLogical(tree) && tree.children.every(isLeaf)) {
    return {
      state: {
        globalOperator: "AND",
        groups: [
          {
            id: makeId(),
            groupOperator: tree.op === "and" ? "AND" : "OR",
            conditions: tree.children.map(
              (c) => leafToCondition(c as FilterTree),
            ),
          },
        ],
      },
      flattened: false,
    };
  }
  // Two-level: top-level AND/OR over group nodes (each itself an
  // AND/OR of leaves).
  if (
    isLogical(tree)
    && tree.children.every(
      (c) => isLogical(c) && (c as { children: FilterTree[] }).children.every(isLeaf),
    )
  ) {
    return {
      state: {
        globalOperator: tree.op === "and" ? "AND" : "OR",
        groups: tree.children.map((g) => {
          const node = g as { op: "and" | "or"; children: FilterTree[] };
          return {
            id: makeId(),
            groupOperator: node.op === "and" ? "AND" : "OR",
            conditions: node.children.map(
              (c) => leafToCondition(c as FilterTree),
            ),
          };
        }),
      },
      flattened: false,
    };
  }
  // Anything else — flatten leaves we can find into one AND group.
  const leaves: FilterCondition[] = [];
  collectLeaves(tree, leaves);
  return {
    state: {
      globalOperator: "AND",
      groups: [
        { id: makeId(), groupOperator: "AND", conditions: leaves },
      ],
    },
    flattened: true,
  };
}


function isLeaf(node: FilterTree): boolean {
  return typeof (node as { field?: unknown }).field === "string";
}


function isLogical(node: FilterTree): node is {
  op: "and" | "or";
  children: FilterTree[];
} {
  return (
    (node as { op?: string }).op === "and"
    || (node as { op?: string }).op === "or"
  );
}


function leafToCondition(leaf: FilterTree): FilterCondition {
  if (!isLeaf(leaf)) {
    return {
      id: makeId(), field: "", operator: "is", value: "",
    };
  }
  const node = leaf as { op: string; field: string; value?: unknown };
  const field = node.field;
  const op = node.op;
  const desc = getField(field);
  let uiOp: UiOperator;
  let value: unknown = node.value;
  switch (op) {
    case "is_null":
      uiOp = "is_empty"; value = ""; break;
    case "is_not_null":
      uiOp = "is_not_empty"; value = ""; break;
    case "lt":
      uiOp = field === "due_date_time" ? "is_before" : "is";
      value = stripIsoTime(value);
      break;
    case "lte":
      uiOp = field === "due_date_time" ? "is_before_or_on" : "is";
      value = stripIsoTime(value);
      break;
    case "gt":
      uiOp = field === "due_date_time" ? "is_after" : "is";
      value = stripIsoTime(value);
      break;
    case "gte":
      uiOp = field === "due_date_time" ? "is_after_or_on" : "is";
      value = stripIsoTime(value);
      break;
    case "contains":
      uiOp = "contains"; break;
    case "in":
      uiOp = "is"; break;
    case "nin":
      uiOp = "is_not"; break;
    case "eq":
      if (field === "due_date_time") {
        uiOp = "is_on"; value = stripIsoTime(value);
      } else if (desc && isMultiValue(desc.valueType)) {
        uiOp = "is";
        value = Array.isArray(value) ? value : [value];
      } else {
        uiOp = "is";
      }
      break;
    case "neq":
      uiOp = "is_not"; break;
    default:
      uiOp = "is";
  }
  return { id: makeId(), field, operator: uiOp, value };
}


function stripIsoTime(value: unknown): unknown {
  if (typeof value === "string" && value.includes("T")) {
    return value.slice(0, 10);
  }
  return value;
}


function collectLeaves(node: FilterTree, out: FilterCondition[]): void {
  if (isLeaf(node)) {
    out.push(leafToCondition(node));
    return;
  }
  if (isLogical(node)) {
    for (const child of node.children) collectLeaves(child, out);
  }
}
